import Service, { service } from '@ember/service';
import type Store from '@ember-data/store';
import type { Audience, GQLAudiences } from '@envoy/components-communication';
import { Audiences } from '@envoy/components-communication';
import type AbilitiesService from 'ember-can/services/abilities';
import {
  type GetAnnouncementAudienceSizesQuery,
  type GetAnnouncementAudienceSizesVariables,
} from 'garaje/graphql/generated/announcement-types';
import announcementAudienceSizesQuery from 'garaje/graphql/queries/AnnouncementAudienceSizesQuery';
import type PluginInstallModel from 'garaje/models/plugin-install';
import type ApolloService from 'garaje/services/apollo-extension';
import type FeatureConfigService from 'garaje/services/feature-config';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { SLACK_V2_PLUGIN_KEY, MSTEAMS_V2_PLUGIN_KEY } from 'garaje/utils/enums';

enum EmployeeAudienceType {
  Self = 'self',
  AllEmployeesWithReservation = 'allEmployeesWithReservation',
  AllEmployeesCheckedIn = 'allEmployeesCheckedIn',
  AllEmployeesAtLocation = 'allEmployeesAtLocation',
  AllEmployeesAtDefaultLocation = 'allEmployeesAtDefaultLocation',
}

enum VisitorAudienceType {
  AllVisitorsWithReservation = 'allVisitorsWithReservation',
  AllVisitorsCheckedIn = 'allVisitorsCheckedIn',
  AllVisitorsSignedOut = 'allVisitorsSignedOut',
}

const audienceTypeMap: Record<EmployeeAudienceType | VisitorAudienceType, Audiences> = {
  [EmployeeAudienceType.Self]: Audiences.Self,
  [EmployeeAudienceType.AllEmployeesWithReservation]: Audiences.AllEmployeesWithReservation,
  [EmployeeAudienceType.AllEmployeesCheckedIn]: Audiences.AllEmployeesCheckedIn,
  [EmployeeAudienceType.AllEmployeesAtLocation]: Audiences.AllEmployeesAtLocation,
  [EmployeeAudienceType.AllEmployeesAtDefaultLocation]: Audiences.AllEmployeesAtDefaultLocation,
  [VisitorAudienceType.AllVisitorsWithReservation]: Audiences.AllVisitorsWithReservation,
  [VisitorAudienceType.AllVisitorsCheckedIn]: Audiences.AllVisitorsCheckedIn,
  [VisitorAudienceType.AllVisitorsSignedOut]: Audiences.AllVisitorsSignedOut,
};

interface SlackTeamsInstalled {
  isSlackInstalled: boolean;
  isTeamsInstalled: boolean;
}

export default class CommunicationsService extends Service {
  @service declare apolloExtension: ApolloService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;
  @service declare abilities: AbilitiesService;
  @service declare featureConfig: FeatureConfigService;
  @service declare store: Store;

  // Checks if the user has access to the visitor audience options
  get showVisitorSelect(): boolean {
    return this.abilities.can('access visitors emergency features for communications');
  }

  // Checks if the user has access to the employee audience options
  get showEmployeeSelect(): boolean {
    return (
      this.abilities.can('access employees emergency features for communications') ||
      this.abilities.can('access employees emergency features at location for communications')
    );
  }

  // Checks if the user has access to the employee scim groups options
  get canSelectScimGroups(): boolean {
    return this.featureConfig.isEnabled('communications.employeeGroups');
  }

  async canSendEmergencyNotifications(): Promise<boolean> {
    const emergencyNotificationConfiguration = await this.state.currentLocation.getEmergencyNotificationConfiguration();
    return emergencyNotificationConfiguration.emergencyNotificationsEnabledAt !== null;
  }

  get canSendTakeovers(): boolean {
    return this.featureConfig.isEnabled('communications.takeovers');
  }

  private getFilteredEmployeeAudiences(): EmployeeAudienceType[] {
    // Define audience groups
    const employeesOnSite = [
      EmployeeAudienceType.AllEmployeesCheckedIn,
      EmployeeAudienceType.AllEmployeesWithReservation,
    ];

    const employeesAtLocation = [
      EmployeeAudienceType.AllEmployeesAtDefaultLocation,
      EmployeeAudienceType.AllEmployeesAtLocation,
    ];

    const selfAudience = [EmployeeAudienceType.Self];
    const allEmployees = [...selfAudience, ...employeesOnSite, ...employeesAtLocation];
    const locationOnlyEmployees = [...selfAudience, ...employeesAtLocation];

    // If user has full employee emergency notifications, show all options
    if (this.abilities.can('access employees emergency features for communications')) {
      return allEmployees;
    }

    // If user has limited employee emergency notifications (at location only), show limited options
    else if (this.abilities.can('access employees emergency features at location for communications')) {
      return locationOnlyEmployees;
    }

    // Default: no employee options
    else {
      return [];
    }
  }

  async getAudiences(locationId: string): Promise<GQLAudiences> {
    try {
      const result = await this.apolloExtension.query<
        GetAnnouncementAudienceSizesQuery,
        GetAnnouncementAudienceSizesVariables,
        'announcementAudienceSizes'
      >(
        {
          query: announcementAudienceSizesQuery,
          variables: {
            locationId,
          },
        },
        'announcementAudienceSizes',
      );

      const { employeeAudienceCount, visitorAudienceCount } = result[0]!;

      // Get filtered employee audience types
      const allowedEmployeeTypes = this.getFilteredEmployeeAudiences();

      // Filter employee audiences based on allowed types
      const filteredEmployeeAudiences = allowedEmployeeTypes.map((type) =>
        this.getAudienceValue(type, employeeAudienceCount),
      );

      return {
        employees: filteredEmployeeAudiences,
        visitors: [
          this.getAudienceValue(VisitorAudienceType.AllVisitorsCheckedIn, visitorAudienceCount),
          this.getAudienceValue(VisitorAudienceType.AllVisitorsWithReservation, visitorAudienceCount),
          this.getAudienceValue(VisitorAudienceType.AllVisitorsSignedOut, visitorAudienceCount),
        ],
      };
    } catch (e) {
      // eslint-disable-next-line
      console.error('Error calling graphql getAudiences query. Error: ', JSON.stringify(e));
      throw e;
    }
  }

  async getSlackTeamsInstalled(): Promise<SlackTeamsInstalled> {
    const plugins = await this.store.query('plugin', {
      filter: {
        notification_channels: true,
      },
      page: {
        limit: 100,
      },
    });

    const slackPlugin = plugins.find((plugin) => plugin.key === SLACK_V2_PLUGIN_KEY);
    const teamsPlugin = plugins.find((plugin) => plugin.key === MSTEAMS_V2_PLUGIN_KEY);
    const validPlugins = [slackPlugin?.id, teamsPlugin?.id].filter(Boolean);
    const page = { limit: 100, offset: 0 };
    const filter = { status: 'active', plugins: validPlugins };

    const pluginInstallsPromises =
      validPlugins.length > 0
        ? this.store.query('plugin-install', { filter, page }).then((pluginInstalls) => pluginInstalls.slice())
        : Promise.resolve([] as PluginInstallModel[]);

    const pluginInstalls = await pluginInstallsPromises;

    return {
      isSlackInstalled: pluginInstalls.some(
        (pluginInstall) => pluginInstall.belongsTo('plugin').id() === slackPlugin?.id,
      ),
      isTeamsInstalled: pluginInstalls.some(
        (pluginInstall) => pluginInstall.belongsTo('plugin').id() === teamsPlugin?.id,
      ),
    };
  }

  private getAudienceValue(type: EmployeeAudienceType | VisitorAudienceType, counts: Record<string, number>): Audience {
    return {
      id: audienceTypeMap[type],
      count: counts[type],
    };
  }
}
